import {ChangeDetectionStrategy, Component, inject, input, OnInit, output} from '@angular/core';
import {customerFormFields, customerResourceType, defaultCustomerData} from "../../configs/customer-modal-config";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {CustomerDialogData} from "../../types/customer-modal.types";
import {CustomerModalService} from "../../services/customer-modal.service";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {ActionDropdownMenuComponent} from "../../../../action-dropdown-menu/action-dropdown-menu.component";
import {RecordUpdateRequestParams} from "../../../../../types/records-table.types";
import {GetRecordPropertyDataByIdParams} from "../../../base-modal/types/base-modal.types";
import {platformUrls, PlatformUrlType} from "../../../../../models/platform-urls-config";
import {BaseModalService} from "../../../services/base-modal.service";

@Component({
  selector: 'app-current-customer-modal',
  standalone: true,
  imports: [
    MatFormField,
    ActionDropdownMenuComponent,
    ReactiveFormsModule,
    MatInput,
    MatLabel
  ],
  templateUrl: './current-customer-modal.component.html',
  styleUrl: './current-customer-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentCustomerModalComponent implements OnInit {
  customerFormGroup = input.required<FormGroup>();
  customerDialogData = input.required<CustomerDialogData>();
  isEditModeActive = input<boolean>(false);
  detachCustomer = output<void>();
  updateLoading = output<boolean>();

  private customerModalService: CustomerModalService = inject(CustomerModalService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public readonly customerFormFields = customerFormFields;

  public async ngOnInit(): Promise<void> {
    await this.getCustomerData();
  }

  public async unbindCustomerClick(): Promise<void> {
    await this.customerModalService.detachCustomer(this.customerDialogData().feedbackId)
      .then(() => this.detachCustomer.emit());
  }

  public async handleUpdateCustomer(key: string): Promise<void> {
    if (!this.customerDialogData().customer.id) {
      return;
    }

    const updateCustomerParams: RecordUpdateRequestParams = {
      recordId: this.customerDialogData().customer.id || 0,
      fieldName: key,
      value: this.customerFormGroup().get(key)?.value,
      path: customerResourceType.path
    };

    await this.customerModalService.updateCustomer(updateCustomerParams)
      .then((customer) => this.customerFormGroup().patchValue(customer));
  }

  public getClasses(): string {
    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public getFieldStyles(key: string): string {
    if (this.isEditModeActive()) { return ''; }

    return this.isActiveLinkRowForUrlType(key) && this.customerFormGroup().get(key)?.value ? 'pointer' : 'default'
  }

  public handleOpenUrlClick(key: string): void {
    if (this.isEditModeActive()) { return; }

    if (!this.isActiveLinkRowForUrlType(key)) { return; }

    if (key === 'phoneNumber') {
      this.baseModalService.initiateCall(key, this.customerFormGroup());
      return;
    }

    this.baseModalService.openUrlInNewTab(key, this.customerFormGroup());
  }

  private async getCustomerData(): Promise<void> {
    if (!this.customerDialogData().customer.id) { return; }

    this.updateLoading.emit(true);
    const params: GetRecordPropertyDataByIdParams = {
      resourceId: this.customerDialogData().customer.id || 0,
      path: customerResourceType.path
    };

    const data = await this.customerModalService.fetchCustomerData(params)
      .catch(() => defaultCustomerData)
      .finally(() => this.updateLoading.emit(false));
    this.customerFormGroup().patchValue(data);
  }

  private isActiveLinkRowForUrlType(type: string): boolean {
    return !this.isEditModeActive() && platformUrls.includes(type as PlatformUrlType);
  }
}
